// ==================== OPTIMIZED CART STATE MANAGER ====================
// This script provides instant cart updates with no delay
// Place this in your Shopify theme's assets folder

(function() {
  'use strict';
  
  window.CartStateManager = {
    // Cache for current cart state
    cartCache: null,
    pendingRefresh: null,
    isRefreshing: false,
    
    // Initialize the cart state manager
    init: function() {
      this.setupEventListeners();
      this.refreshCartState();
    },
    
    // Setup event listeners with debouncing
    setupEventListeners: function() {
      // Handle browser back button
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.debouncedRefresh();
        }
      });
      
      // Handle visibility change (only if page was hidden for >1 second)
      let lastHidden = 0;
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          lastHidden = Date.now();
        } else if (Date.now() - lastHidden > 1000) {
          this.debouncedRefresh();
        }
      });
      
      // Setup cross-tab communication
      this.setupCrossTabCommunication();
      
      // Listen for custom cart add events
      document.addEventListener('cart:item-added', (e) => {
        this.handleItemAdded(e.detail);
      });
      
      document.addEventListener('cart:item-updated', (e) => {
        this.handleItemUpdated(e.detail);
      });
    },
    
    // Debounced refresh to prevent multiple simultaneous calls
    debouncedRefresh: function() {
      clearTimeout(this.pendingRefresh);
      this.pendingRefresh = setTimeout(() => {
        this.refreshCartState();
      }, 150);
    },
    
    // Handle item added with optimistic update
    handleItemAdded: function(detail) {
      if (!detail) return;
      
      // Optimistically update cart count immediately
      const currentCount = this.getCartCount();
      const quantityAdded = detail.quantity || 1;
      this.updateHeaderCartCount({ item_count: currentCount + quantityAdded });
      
      // Then refresh from server to ensure accuracy
      this.debouncedRefresh();
      
      // Broadcast to other tabs
      if (this.broadcastCartUpdate) {
        this.broadcastCartUpdate('item-added', detail);
      }
    },
    
    // Handle item updated with optimistic update
    handleItemUpdated: function(detail) {
      if (!detail) return;
      
      // Optimistically update based on delta
      if (detail.delta) {
        const currentCount = this.getCartCount();
        this.updateHeaderCartCount({ item_count: currentCount + detail.delta });
      }
      
      // Refresh from server
      this.debouncedRefresh();
      
      // Broadcast to other tabs
      if (this.broadcastCartUpdate) {
        this.broadcastCartUpdate('item-updated', detail);
      }
    },
    
    // Get current cart count from DOM
    getCartCount: function() {
      const countElement = document.querySelector('.header-cart-link span');
      return countElement ? parseInt(countElement.textContent) || 0 : 0;
    },
    
    // Setup cross-tab communication
    setupCrossTabCommunication: function() {
      let broadcastChannel = null;
      
      try {
        broadcastChannel = new BroadcastChannel('cart_updates');
      } catch (e) {
        // BroadcastChannel not supported
      }
      
      this.broadcastCartUpdate = function(action, data) {
        const message = {
          action: action,
          data: data,
          timestamp: Date.now()
        };
        
        if (broadcastChannel) {
          try {
            broadcastChannel.postMessage(message);
          } catch (e) {
            console.error('Broadcast failed:', e);
          }
        }
        
        try {
          localStorage.setItem('cart_update_broadcast', JSON.stringify(message));
          setTimeout(() => localStorage.removeItem('cart_update_broadcast'), 100);
        } catch (e) {
          // Silent fail
        }
      };
      
      if (broadcastChannel) {
        broadcastChannel.onmessage = (event) => {
          this.debouncedRefresh();
        };
      }
      
      window.addEventListener('storage', (e) => {
        if (e.key === 'cart_update_broadcast' && e.newValue) {
          this.debouncedRefresh();
        }
      });
    },
    
    // Refresh cart state from server
    refreshCartState: function() {
      // Prevent multiple simultaneous refreshes
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          this.cartCache = cart;
          this.updateHeaderCartCount(cart);
          this.updateProductForms(cart);
          this.updateProductPage(cart);
          
          // Dispatch custom event
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { itemCount: cart.item_count, cart: cart }
          }));
        })
        .catch(e => console.error('Error refreshing cart:', e))
        .finally(() => {
          this.isRefreshing = false;
        });
    },
    
    // Update cart count in header
    updateHeaderCartCount: function(cart) {
      const selectors = [
        '.header-cart-link span',
        '.cart-count',
        '.cart-link span',
        '[data-cart-count]',
        '#cart-count'
      ];
      
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.textContent = cart.item_count;
          if (element.hasAttribute('data-cart-count')) {
            element.setAttribute('data-cart-count', cart.item_count);
          }
        });
      });
    },
    
    // Update product forms (simplified version)
    updateProductForms: function(cart) {
      document.querySelectorAll('.product-form').forEach(form => {
        const variantSelect = form.querySelector('.product-variant-select, .variant-select-grid');
        const hiddenInput = form.querySelector('input[name="id"]');
        const variantId = variantSelect ? variantSelect.value : (hiddenInput ? hiddenInput.value : null);
        
        if (!variantId) return;
        
        const cartItem = cart.items.find(item => item.variant_id == variantId);
        const quantity = cartItem ? cartItem.quantity : 0;
        
        // Update UI elements
        const addToCartBtn = form.querySelector('.add-to-cart-btn, .add-to-cart-btn-grid');
        const quantitySelector = form.querySelector('.product-form__quantity, .quantity-selector-grid');
        const quantityDisplay = form.querySelector('.quantity-display, .quantity-display-grid');
        
        if (quantity > 0) {
          if (addToCartBtn) addToCartBtn.classList.add('hidden');
          if (quantitySelector) {
            quantitySelector.classList.remove('hidden');
            quantitySelector.classList.add('active');
          }
          if (quantityDisplay) quantityDisplay.textContent = quantity;
        } else {
          if (addToCartBtn) addToCartBtn.classList.remove('hidden');
          if (quantitySelector) {
            quantitySelector.classList.add('hidden');
            quantitySelector.classList.remove('active');
          }
          if (quantityDisplay) quantityDisplay.textContent = '1';
        }
      });
    },
    
    // Update product page
    updateProductPage: function(cart) {
      const quantityWrapper = document.querySelector('.quantity-wrapper');
      const quantityInput = document.querySelector('.quantity-input');
      
      if (!quantityWrapper || !quantityInput) return;
      
      const currentVariantButton = document.querySelector('.variant-button.active, .variant-button.selected');
      if (!currentVariantButton) return;
      
      const variantId = currentVariantButton.dataset.variantId;
      const cartItem = cart.items.find(item => item.variant_id == variantId);
      
      if (cartItem && cartItem.quantity > 0) {
        quantityWrapper.style.display = 'flex';
        quantityInput.value = cartItem.quantity;
      } else {
        quantityWrapper.style.display = 'none';
        quantityInput.value = 1;
      }
    }
  };
  
  // Initialize immediately if DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.CartStateManager.init();
    });
  } else {
    window.CartStateManager.init();
  }
})();
